import { SPECIAL_SYMBOL_LIST } from '../../Search/constants/deepSearch';
import {
  CUSTOM_TAG_VALUE,
  SYSTEM_TAG_BRANDS,
  SYSTEM_TAG_CC,
  SYSTEM_TAG_PEOPLE,
  SYSTEM_TAG_TEXT,
  TYPE_ALL,
} from '../constants/label';
import { DIVIDER, END, HIGHLIGHT_MARK_REG_EXP, START } from '../constants/string';

import { addHighlightMarks, escapeStringRegexp } from './string';

const isAcross = (aX, aLength, bX) => bX >= aX && bX <= aLength;

const memoResult = (fn, compareFnKey) => {
  const memo$ = {};

  return (...args) => {
    const key = compareFnKey(...args);

    if (!memo$[key]) {
      memo$[key] = fn(...args);
    }

    return memo$[key];
  };
};

export const getItemId = (type, value, color, name, time, endTime) =>
  ['item', type, color, name, encodeURIComponent(value), time ?? 0, endTime ?? time].join('-');

const mergeUnique = (source, next) =>
  next.reduce(
    (acc, log) => {
      const alreadyInArray = acc.some((log$) =>
        ['value', 'type', 'color', 'name', 'time', 'endTime'].every((key) => log$[key] === log[key]),
      );

      return !alreadyInArray ? acc.concat({ ...log }) : acc;
    },
    [...source],
  );

const mergeDuplicates = (logList) => {
  const result = [];

  logList.forEach((item) => {
    const sib = result.find(({ time }) => item.time === time);

    if (sib && sib.endTime === item.endTime) {
      sib.list = mergeUnique(sib.list, item.list);
    } else {
      result.push(item);
    }
  });

  return result;
};

const compressByTime = (logList, queries) => {
  const compressedLogList = [];

  const addInItem = (log, log$) => {
    if (log.endTime && log$.endTime) {
      if (isAcross(log.time, log.endTime, log$.time)) {
        compressedLogList.push({
          time: Math.max(log$.time, log.time),
          endTime: Math.min(log$.endTime, log.endTime),
          list: mergeUnique(log.list, log$.list),
        });
      }
    } else if (log.endTime) {
      if (log$.time >= log.time && log$.time <= log.endTime) {
        compressedLogList.push({
          time: log$.time,
          endTime: null,
          list: mergeUnique(log.list, log$.list),
        });
      }
    } else if (log$.endTime) {
      if (log.time >= log$.time && log.time <= log$.endTime) {
        compressedLogList.push({
          time: log.time,
          endTime: null,
          list: mergeUnique(log.list, log$.list),
        });
      }
    } else if (log.time === log$.time) {
      compressedLogList.push({
        time: log.time,
        endTime: null,
        list: mergeUnique(log.list, log$.list),
      });
    }
  };

  for (let i = 0; i < logList.length; i += 1) {
    const targetLog = logList[i];

    const list = targetLog.list.filter(
      (log$) =>
        [SYSTEM_TAG_TEXT, SYSTEM_TAG_CC].some((type$) => log$.type === type$) &&
        queries.every((query) => log$.highlights.some(({ id }) => query === id)),
    );

    if (list.length) {
      compressedLogList.push({
        time: targetLog.time,
        endTime: targetLog.endTime,
        list,
      });
    }

    for (let k = i + 1; k < logList.length; k += 1) {
      const nextLog = logList[k];

      addInItem(targetLog, nextLog);
    }
  }

  return mergeDuplicates(compressedLogList)
    .filter((item) => {
      const ids = item.list.reduce((acc, { highlights }) => [...acc, ...highlights.map(({ id }) => id)], []);

      return queries.every((query) => ids.includes(query));
    })
    .map((item) => {
      const endTime = item.time === item.endTime ? null : item.endTime;

      return {
        ...item,
        endTime,
        list: item.list.map((log) => ({
          ...log,
          time: item.time,
          endTime,
        })),
      };
    });
};

export const mergeTagsByTime = (logList, queries, includeAllTags) => {
  const formattedLogList = logList.reduce((acc, logSource) => {
    const log = {
      ...logSource,
      highlights: logSource.highlights.slice(),
    };

    const itemIndex = acc.findIndex(({ time, endTime }) => time === log.time && endTime === log.endTime);

    if (itemIndex === -1) {
      return acc.concat({
        time: log.time,
        endTime: log.endTime,
        list: [log],
      });
    }

    acc[itemIndex].list.push(log);

    return acc;
  }, []);

  const resultItems =
    includeAllTags && queries.length >= 2 ? compressByTime(formattedLogList, queries) : formattedLogList;

  return resultItems.map((item) => ({
    ...item,
    list: item.list.map((log) => ({
      ...log,
      itemId: getItemId(log.type, log.value, log.color, log.name, log.time, log.endTime),
    })),
  }));
};

export const getTabList = (dsTags) => {
  const tabsToShow = [
    {
      title: 'People',
      type: SYSTEM_TAG_PEOPLE,
    },
    {
      title: 'Brands',
      type: SYSTEM_TAG_BRANDS,
    },
    {
      title: 'Transcript',
      type: SYSTEM_TAG_CC,
    },
    {
      title: 'Text',
      type: SYSTEM_TAG_TEXT,
    },
  ].filter(({ type }) => dsTags.includes(type));

  if (dsTags.length > 1) {
    tabsToShow.unshift({
      title: 'All',
      type: TYPE_ALL,
    });
  }

  return tabsToShow;
};

const getHLRegExp = (highlights) => {
  const hlPart = highlights.map((value) => `\\b${escapeStringRegexp(value)}\\b`).join('|');

  return new RegExp(`(${hlPart})`, 'g');
};

export const getSplitRegExp = memoResult(
  (stopWords) => {
    const allRegSymbols = [
      stopWords.map((value) => `\\b${value}\\b`).join('|'),
      `[${SPECIAL_SYMBOL_LIST.join('')}]`,
    ].join('|');

    return new RegExp(`${END}${DIVIDER}((${allRegSymbols})+)?${DIVIDER}${START}`, 'gi');
  },
  (stopWords) => stopWords.slice().sort().join('-'),
);

export const getFormattedLogList = memoResult(
  (string, highlights, stopWords, itemId) => {
    const resultString =
      string === highlights[0]
        ? addHighlightMarks(string)
        : string
            .replace(getHLRegExp(highlights), (match, $1) => ($1 ? addHighlightMarks($1) : ''))
            .replace(getSplitRegExp(stopWords), (match, $1) => $1);

    return resultString
      .split(DIVIDER)
      .map((text, index) => {
        let isHighlighted = false;

        const value = text.replace(HIGHLIGHT_MARK_REG_EXP, () => {
          isHighlighted = true;

          return '';
        });

        return {
          id: `${itemId}-${+index}`,
          isHighlighted,
          value,
        };
      })
      .filter(({ value }) => !!value);
  },
  (value, highlights, stopWords) => [value, ...highlights, ...stopWords].join('-'),
);

export const getAvailableLogs = (data, queries) =>
  data.reduce((acc, item) => {
    const highlightsList = item.highlights.filter(({ id }) => queries.some((id$) => id === id$));

    return !highlightsList.length
      ? acc
      : acc.concat({
          ...item,
          highlights: highlightsList,
        });
  }, []);

export const getLogCountCollection = (data, queries, isIncludeAllTags) => {
  const defaultCollection = {
    [TYPE_ALL]: 0,
    [SYSTEM_TAG_PEOPLE]: 0,
    [SYSTEM_TAG_BRANDS]: 0,
    [SYSTEM_TAG_TEXT]: 0,
    [SYSTEM_TAG_CC]: 0,
    [CUSTOM_TAG_VALUE]: 0,
  };

  if (isIncludeAllTags) {
    const lCollection = { ...defaultCollection };

    Object.keys(defaultCollection).forEach((type) => {
      const preparedData = type === TYPE_ALL ? data : data.filter((log) => log.type === type);

      mergeTagsByTime(getAvailableLogs(preparedData, queries), queries, isIncludeAllTags)
        .map((item$) => ({
          ...item$,
          list: item$.list.filter((log) => type === TYPE_ALL || log.type === type),
        }))
        .forEach((item) => {
          const ids = item.list.reduce((acc, { highlights }) => [...acc, ...highlights.map(({ id }) => id)], []);

          if (queries.every((query) => ids.includes(query))) {
            lCollection[type] += item.list.length;
          }
        });
    });

    return lCollection;
  }

  const countsByType = mergeTagsByTime(getAvailableLogs(data, queries), queries, isIncludeAllTags).reduce(
    (acc, item) => {
      item.list.forEach((log) => {
        acc[log.type] += 1;
      });

      return acc;
    },
    { ...defaultCollection },
  );

  const sumCount = Object.keys(countsByType).reduce((acc, key) => acc + countsByType[key], 0);

  return {
    ...countsByType,
    [TYPE_ALL]: sumCount,
  };
};
